import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { UploadButton } from "@/pages/admin/UploadButton";

const EMPTY = { name: "", area: "", photo_url: "", active: true };
const field = "mt-1 h-11 w-full rounded-xl border border-gold/50 bg-white px-3 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30";

export const CommitteeDialog = ({ open, onOpenChange, committee, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(committee ? { name: committee.name, area: committee.area, photo_url: committee.photo_url, active: committee.active } : EMPTY); }, [open, committee]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      if (committee) await api.put(`/admin/committees/${committee.id}`, form);
      else await api.post("/admin/committees", form);
      toast.success(committee ? "Committee updated" : "Committee added — live on the public site");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="committee-dialog" className="w-[calc(100%-2rem)] max-w-md rounded-2xl bg-ivory">
        <DialogHeader><DialogTitle className="font-display text-2xl text-maroon">{committee ? "Edit committee" : "Add committee"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm font-medium">Name<input value={form.name} onChange={set("name")} data-testid="committee-name-input" className={field} /></label>
          <label className="block text-sm font-medium">Area<input value={form.area} onChange={set("area")} data-testid="committee-area-input" className={field} /></label>
          <div className="text-sm font-medium">
            Photo (optional)
            <div className="mt-2 flex items-center gap-3">
              {form.photo_url ? <img src={mediaUrl(form.photo_url)} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <span className="grid h-14 w-14 place-items-center rounded-lg bg-gold/20 text-xs text-ink-muted">None</span>}
              <UploadButton testId="committee-photo-upload" label="Upload" onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))} />
              {form.photo_url && <button type="button" className="text-xs text-maroon underline" data-testid="committee-photo-clear" onClick={() => setForm((f) => ({ ...f, photo_url: "" }))}>Remove</button>}
            </div>
          </div>
          <label className="flex items-center justify-between rounded-xl bg-white p-3 text-sm font-medium">
            Active on public site
            <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="committee-active-input" />
          </label>
          <button type="button" onClick={save} disabled={busy || !form.name.trim()} data-testid="committee-save-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-forest font-semibold text-white disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
